/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      function modifier — keeps an element at its last settled height
 *              while a recalculation runs, so content that is dropped at the
 *              start of the run and comes back at its end (the summary's
 *              deposit / «pay on site» callout and the «you will pay» line,
 *              2-3 lines that depend on totalAPI) does not collapse and push
 *              back the siblings below it (privacy box, confirm button)
 *   args:      positional [busy] (boolean: the recalculation is running)
 *   uses:      ResizeObserver (records the settled height), ember-modifier
 *   consumers: booking-index-nuova.hbs (.nv-bk-sum, busy = getTotalTask.isRunning)
 *   gotchas:   (1) a function modifier re-runs teardown + setup on every arg
 *              change, so the settled height lives in a WeakMap keyed by the
 *              element, not in the closure. (2) Setup runs synchronously after
 *              the DOM update and before paint: the min-height is in place
 *              before the shorter content is ever shown. (3) No settled height
 *              yet (first run already busy) = nothing to hold. (4) When the run
 *              ends the hold is dropped: a legitimately shorter result shrinks
 *              once, as it should.
 */
import { modifier } from 'ember-modifier';

const settledHeight = new WeakMap();

export default modifier(function holdHeightWhileBusy(element, [busy]) {
  if (busy) {
    const height = settledHeight.get(element);
    if (height) {
      element.style.minHeight = `${height}px`;
    }
    return () => {
      element.style.minHeight = '';
    };
  }

  element.style.minHeight = '';
  settledHeight.set(element, element.offsetHeight);
  if (typeof ResizeObserver !== 'function') {
    return undefined;
  }
  const observer = new ResizeObserver(() => {
    settledHeight.set(element, element.offsetHeight);
  });
  observer.observe(element);
  return () => observer.disconnect();
});
